import React, { useState } from 'react'
import styled from 'styled-components'
import { cronosTokens } from '@pancakeswap/tokens'
import { Button, Text } from '@pancakeswap/uikit'
import { useTranslation } from '@pancakeswap/localization'
import { ToastDescriptionWithTx } from 'components/Toast'
import useCatchTxError from 'hooks/useCatchTxError'
import useToast from 'hooks/useToast'
import { BIG_ZERO } from 'utils/bigNumber'
import getBoredCandyNftImageUrl from 'utils/getNftImageUrl'
import { getBalanceNumber } from 'utils/formatBalance'
import { LockData } from '../../types'
import { getLockApr } from '../../hooks/helpers'
import useLockConfig from '../../hooks/useLockConfig'
import useLockReward from '../../hooks/useLockReward'
import useLockToken from '../../hooks/useLockToken'
import NftItem from '../Nft/NftItem'

const StyledLockCardDetails = styled.div`
  margin: 16px 12px 0;
`

const Item = styled.div`
  align-items: center;
  color: ${({ theme }) => theme.colors.secondary};
  display: flex;
  margin-bottom: 4px;
`

const Display = styled(Text) <{ subHeader?: boolean }>`
  font-size: ${({ subHeader }) => subHeader ? '13px' : '15px'};
  margin-left: ${({ subHeader }) => subHeader ? '8px' : '0px'};
  flex: 1;
`

const NftRow = styled.div`
  position: relative;
  display: flex;
  width: 100%;
  justify-content: flex-start;
  flex-wrap: nowrap;
  gap: 8px;
  overflow-x: auto;
  overflow-y: hidden;

  &::-webkit-scrollbar {
    height: 8px;
  }
`

const LockCardDetails: React.FC<{ data: LockData }> = ({ data }) => {
  const { t } = useTranslation()
  const candyToken = cronosTokens.candy
  const [status, setStatus] = useState(false)

  const {
    lid,
    lockAt,
    duration,
    amount,
    nfts,
    unlocked
  } = data
  
  const { reward } = useLockReward(lid, status)
  const { onClaim, onUnlock } = useLockToken()
  const { toastSuccess } = useToast()
  const { fetchWithCatchTxError, loading: pendingTx } = useCatchTxError()

  const { aprRates, boostRates } = useLockConfig()

  const toggleStatus = () => {
    setStatus(!status)
  }

  return (
    <>
      <StyledLockCardDetails>
        <Item>
          <Display>{t('Lock Start Date')}</Display>
          <Text>
            {`${new Date(lockAt * 1000).toLocaleString('en-US', {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              timeZone: 'UTC',
            })} (UTC)`
            }
          </Text>
        </Item>
        <Item>
          <Display>{t('Lock End Date')}</Display>
          <Text>
            {`${new Date((lockAt + duration) * 1000).toLocaleString('en-US', {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              timeZone: 'UTC',
            })} (UTC)`
            }
          </Text>
        </Item>
        <Item>
          <Display>{t('Lock Amount')}</Display>
          <Text>{getBalanceNumber(amount, candyToken.decimals)
            .toLocaleString('en-US', { maximumFractionDigits: 3 })} {candyToken.symbol}</Text>
        </Item>
        <Item>
          <Display>{t('Boosting Nfts')}</Display>
          {nfts.length === 0 ? (
            <Text>No NFTs Locked</Text>
          ) : <></>}
        </Item>
        {nfts.length > 0 && (
          <NftRow>
            {nfts.map((nft) => (
              <NftItem key={`Boost NFT item - ${nft}`} id={nft} image={getBoredCandyNftImageUrl(nft)} />
            ))}
          </NftRow>
        )}
        <Item>
          <Display>{t('APR')}</Display>
          <Text>{getLockApr(aprRates, boostRates, duration, nfts)} %</Text>
        </Item>
        <Item>
          <Display>{t('Available Reward')}</Display>
          <Text>{getBalanceNumber(reward, candyToken.decimals)
            .toLocaleString('en-US', { maximumFractionDigits: 5 })} {candyToken.symbol}</Text>
        </Item>
        <Item style={{ gap: '8px', marginTop: '16px' }}>
          <Button
            disabled={pendingTx || reward.isEqualTo(BIG_ZERO)}
            style={{ flex: '1' }}
            onClick={async () => {
              const receipt = await fetchWithCatchTxError(() => {
                return onClaim(lid)
              })
              if (receipt?.status) {
                toastSuccess(
                  `${t('Claimed')}!`,
                  <ToastDescriptionWithTx txHash={receipt.transactionHash}>
                    {t('Claimed tokens have been sent to your wallet!')}
                  </ToastDescriptionWithTx>,
                )
                toggleStatus()
              }
            }}
          >Claim Reward</Button>
          <Button
            disabled={pendingTx || unlocked || (lockAt + duration > Date.now() / 1000)}
            style={{ flex: '1' }}
            onClick={async () => {
              const receipt = await fetchWithCatchTxError(() => {
                return onUnlock(lid)
              })
              if (receipt?.status) {
                toastSuccess(
                  `${t('Unlocked')}!`,
                  <ToastDescriptionWithTx txHash={receipt.transactionHash}>
                    {t('Unlocked tokens have been sent to your wallet!')}
                  </ToastDescriptionWithTx>,
                )
                toggleStatus()
              }
            }}
          >Unlock</Button>
        </Item>
      </StyledLockCardDetails>
    </>
  )
}

export default LockCardDetails
